import { Ban, Check, Loader2, Printer, RotateCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PrintTicketStatus } from '@/api/qrCode.api';

interface PrintProgressProps {
  tickets: PrintTicketStatus[];
  isPrinting: boolean;
  /** The batch never came back at all — printer unreachable, request refused. */
  batchError?: string | null;
  onRetry?: () => void;
  onReprintOne?: (qrId: string) => void;
}

export function PrintProgress({
  tickets,
  isPrinting,
  batchError = null,
  onRetry,
  onReprintOne,
}: PrintProgressProps) {
  const failed = tickets.filter((t) => t.status === 'FAILED');
  // Refused outright (#1746): cancelled/refunded, or another visit day. Kept
  // OUT of `failed` on purpose — these never reached the printer and retrying
  // them cannot help, so they get their own reason instead of a retry button.
  const refused = tickets.filter((t) => t.status === 'REFUSED');

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink-600">
        <Printer className="h-4 w-4 text-primary" />
        Mencetak tiket
        {isPrinting && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
      </div>

      <ul className="max-h-48 space-y-1.5 overflow-y-auto pos-scroll">
        {tickets.map((t) => {
          const ok = t.status === 'PRINTED';
          const bad = t.status === 'FAILED';
          // Print-once: skipped because it already came out (or may have).
          const skipped = t.status === 'ALREADY_PRINTED';
          // Refused: the ticket itself is the problem, not the printer.
          const stopped = t.status === 'REFUSED';
          return (
            <li
              key={t.id}
              className="flex items-start justify-between rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              <span className="min-w-0">
                <span className="tnum block truncate text-ink-600">{t.id}</span>
                {skipped && (
                  <span className="block text-xs text-amber-600">
                    {t.message ?? 'Sudah dicetak sebelumnya.'}
                  </span>
                )}
                {/* Per ticket, because an invoice can be MIXED: today's bands
                    print while a refunded one or next Saturday's is refused,
                    and each refusal has its own reason. */}
                {stopped && (
                  <span className="block text-xs text-destructive">
                    {t.message ?? 'Gelang tidak dapat dicetak untuk tiket ini.'}
                  </span>
                )}
                {/* A real print failure used to show no reason at all here. */}
                {bad && t.message && (
                  <span className="block text-xs text-destructive">{t.message}</span>
                )}
              </span>
              <span className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex h-6 w-6 items-center justify-center rounded-full',
                    ok && 'bg-success/15 text-success',
                    bad && 'bg-destructive/15 text-destructive',
                    skipped && 'bg-amber-100 text-amber-600',
                    stopped && 'bg-destructive/15 text-destructive',
                    !ok && !bad && !skipped && !stopped && 'bg-secondary text-ink-400',
                  )}
                >
                  {ok || skipped ? (
                    <Check className="h-4 w-4" />
                  ) : bad ? (
                    <X className="h-4 w-4" />
                  ) : stopped ? (
                    <Ban className="h-4 w-4" />
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                </span>
                {bad && onReprintOne && (
                  <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => onReprintOne(t.id)}>
                    <RotateCw className="h-3.5 w-3.5" /> Ulang
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {batchError && onRetry && (
        <div className="rounded-md border-2 border-destructive/30 bg-destructive/5 p-3">
          {/* The payment already succeeded — say so first, because the only
              thing on screen otherwise is a printer that did not answer. */}
          <p className="text-sm font-medium text-destructive">
            Tiket gagal dicetak — printer tidak menjawab. Transaksi sudah berhasil dan tiket sudah
            terbit; cetak ulang dari menu Pesanan kapan saja.
          </p>
          <p className="mt-1 text-xs text-ink-400">{batchError}</p>
          <Button variant="outline" size="sm" className="mt-2 gap-1.5" onClick={onRetry}>
            <RotateCw className="h-4 w-4" /> Coba cetak lagi
          </Button>
        </div>
      )}

      {refused.length > 0 && (
        <div className="rounded-md border-2 border-destructive/30 bg-destructive/5 p-3">
          {/* No retry button: these were refused before anything was sent to
              the printer, and the refusal stands for every further attempt.
              The reason is on each row above — a mixed invoice can hold two
              different ones. */}
          <p className="text-sm font-medium text-destructive">
            {refused.length} tiket tidak dapat dicetak (dibatalkan/refund, atau bukan untuk hari
            ini). Lihat keterangan di tiap tiket — mencoba lagi tidak akan berhasil.
          </p>
        </div>
      )}

      {failed.length > 0 && onRetry && (
        <div className="rounded-md border-2 border-destructive/30 bg-destructive/5 p-3">
          <p className="text-sm font-medium text-destructive">
            {failed.length} tiket gagal dicetak. Transaksi sudah berhasil — cetak ulang kapan saja
            dari menu Pesanan.
          </p>
          <Button variant="outline" size="sm" className="mt-2 gap-1.5" onClick={onRetry}>
            <RotateCw className="h-4 w-4" /> Cetak ulang semua yang gagal
          </Button>
        </div>
      )}
    </div>
  );
}
